import React from "react";
import {
    useGetFacilitiesV2Query,
    type ApiError,
} from "@/features/api/apiSlice";
import { UnitItem } from "@/components/units/unit/UnitItem";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { UnitItemLoading } from "@/components/units/unit/UnitItemLoading";
import { NoUnitsAvailable } from "./NoUnitsAvailable";
import { UnitError } from "@/components/units/unit/UnitError";
import type { UnitGroup } from "@/types";
import { FacilitiesContext } from "@/context/FacilitiesContext";
import { UnavailableUnitItem } from "@/components/units/unit/UnavailableUnitItem";
import { filterUnitsByFeatureCategory } from "@/utils/units/unitFilters";
import { sortUnitsByProperty } from "@/utils/units/unitSorting";
import ErrorBoundary from "../ErrorBoundary";
import type { SerializedError } from "@reduxjs/toolkit";

type UnitTableProps = {
    facilityIds: number[];
    units: UnitGroup[];
    unitsError: ApiError | SerializedError | undefined;
    unitsIsLoading: boolean;
    selectedFeatures: number[];
    selectedCategories: number[];
    sort: string;
};

// Helper to check if error is ApiError
const isApiError = (error: ApiError | SerializedError): error is ApiError => {
    return "status" in error;
};

export const UnitTable = ({
    facilityIds,
    units,
    unitsError,
    unitsIsLoading,
    selectedFeatures,
    selectedCategories,
    sort,
}: UnitTableProps) => {
    const displayOptions = useDisplayOptionContext();
    const storedApiKey = useSelector((state: RootState) => state.apiKey.apiKey);

    const facilityParams = { ids: facilityIds.join(","), withSettings: true };

    // Fetch unit groups and facilities using API

    // Extract sorting property and order from the sort string
    const [sortProperty, sortOrder] = sort.split(":") as [
        "vacancy" | "price" | "area",
        "asc" | "desc",
    ];

    const { data: facilitiesData } = useGetFacilitiesV2Query(facilityParams, {
        skip: !storedApiKey,
    });

    const urlParams = new URLSearchParams(window.location.search);
    const unitTypeName = urlParams.get("unit_type_name");

    // Compute filtered and sorted units
    const filteredAndSortedUnits = React.useMemo(() => {
        const filteredUnits = filterUnitsByFeatureCategory(
            units.filter((unitGroup: UnitGroup) => {
                if (
                    unitTypeName &&
                    unitGroup.representative_unit.unit_type_name !==
                        unitTypeName
                ) {
                    return false;
                }
                return (
                    displayOptions.unavailableUnits || unitGroup.available > 0
                );
            }) || [],
            selectedFeatures,
            selectedCategories,
        );

        return sortUnitsByProperty(filteredUnits, sortProperty, sortOrder);
    }, [
        units,
        selectedFeatures,
        selectedCategories,
        sortProperty,
        sortOrder,
        unitTypeName,
        displayOptions.unavailableUnits,
    ]);

    // Render UI
    if (unitsError) {
        const errorMessage = isApiError(unitsError)
            ? `Error ${unitsError.status}: ${unitsError.message}`
            : `Error: ${unitsError.message || "An unknown error occurred"}`;

        return (
            <div className="unit-table">
                <UnitError />
                <p>{errorMessage}</p>
            </div>
        );
    }

    if (unitsIsLoading) {
        return (
            <div className="unit-table">
                {Array.from({ length: 6 }, (_, index) => (
                    <UnitItemLoading
                        layout={displayOptions.tableLayout ?? "row"}
                        key={`loading-item-${index}`}
                    />
                ))}
            </div>
        );
    }

    if (!units || units.length === 0) {
        return (
            <div className="unit-table">
                <UnitError />
            </div>
        );
    }

    if (filteredAndSortedUnits.length === 0) {
        return (
            <div className="unit-table">
                <ErrorBoundary>
                    <NoUnitsAvailable />
                </ErrorBoundary>
            </div>
        );
    }

    const facilitiesContextValue = {
        facilities: facilitiesData?.data || [],
    };

    return (
        <div className="unit-table">
            <FacilitiesContext.Provider value={facilitiesContextValue}>
                {filteredAndSortedUnits.map((unitGroup: UnitGroup) => {
                    return unitGroup.available === 0 &&
                        unitGroup.show_nearby_units ? (
                        <UnavailableUnitItem
                            key={unitGroup.representative_unit.id}
                            unitGroup={unitGroup}
                            layout={displayOptions.tableLayout ?? "row"}
                        />
                    ) : (
                        <UnitItem
                            key={unitGroup.representative_unit.id}
                            unitGroup={unitGroup}
                            layout={displayOptions.tableLayout ?? "row"}
                        />
                    );
                })}
            </FacilitiesContext.Provider>
        </div>
    );
};
